import { alpha, Box, CircularProgress, Popover, useTheme } from '@mui/material';
import {
  DROPDOWN_TRIGGER_GAP_PX,
  getDropdownPaperStyles,
} from '@onevision-resources/design-theme';
import { Command } from 'cmdk';
import { Check, ChevronRight, Plus, Search } from 'lucide-react';
import {
  CSSProperties,
  forwardRef,
  KeyboardEvent,
  MutableRefObject,
  ReactElement,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { isNonNullish } from 'remeda';
import { SelectTrigger } from 'src/components/SelectTrigger';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import {
  CommandRoot,
  CreateActionDivider,
  CreateActionRow,
  HotkeyGlyph,
  MenuStatus,
  OptionCheck,
  OptionCheckbox,
  OptionHint,
  OptionRow,
  SearchInput,
  SearchRow,
} from './select-menu.elements';
import { substringFilter } from './select-menu.filter';
import {
  SelectMenuOption,
  SelectMenuProps,
  SelectMenuTriggerProps,
} from './select-menu.types';
import { useCreateActionFocus } from './use-create-action-focus';

// Keeps cmdk's keyboard handling active when the visible search input is disabled.
const HIDDEN_INPUT_STYLE: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  border: 0,
  opacity: 0,
  pointerEvents: 'none',
  fontSize: '1rem',
};

// Consecutive options sharing a `group` become one section; the caller's order
// is preserved, and options without a group render bare.
const groupRuns = (options: SelectMenuOption[]) => {
  const runs: [string | undefined, SelectMenuOption[]][] = [];
  options.forEach((option) => {
    const current = runs[runs.length - 1];
    if (current && current[0] === option.group) {
      current[1].push(option);
    } else {
      runs.push([option.group, [option]]);
    }
  });
  return runs;
};

const setRef = (ref: Ref<HTMLElement> | undefined, node: HTMLElement | null) => {
  if (typeof ref === 'function') {
    ref(node);
  } else if (ref) {
    (ref as MutableRefObject<HTMLElement | null>).current = node;
  }
};

/** Dropdown/select rendered as a filterable command menu (cmdk inside a MUI Popover). */
export const SelectMenu = forwardRef<HTMLButtonElement, SelectMenuProps>(
  (
    {
      options,
      value,
      defaultValue,
      onChange,
      multiple = false,
      values = [],
      onValuesChange,
      placeholder = 'Select…',
      triggerLabel: triggerLabelOverride,
      triggerContent,
      triggerIcon,
      clearable = false,
      onClear,
      triggerLoading = false,
      search = false,
      searchPlaceholder = 'Search…',
      searchValue,
      onSearchChange,
      loading = false,
      header,
      hasMore = false,
      onLoadMore,
      loadingMore = false,
      hotkey,
      emptyText = 'No results found.',
      renderTrigger,
      tooltip,
      variant = 'outlined',
      sectioned = false,
      plainOptionIcons = false,
      disabled = false,
      menuWidth = '17rem',
      menuSx,
      open: openProp,
      onOpenChange,
      onOptionHover,
      onOptionHoverEnd,
      onKeyDownCapture,
      label = 'Options',
      defaultOpen = false,
      transitionDuration,
      sx = {},
      children,
      createAction,
    },
    forwardedRef,
  ) => {
    const theme = useTheme();
    const triggerId = useId();
    const listboxId = useId();
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const open = openProp ?? internalOpen;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [internalValue, setInternalValue] = useState<string | null>(defaultValue ?? null);
    // Uncontrolled search; cleared on reopen.
    const [query, setQuery] = useState('');

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;
    const selectedOption = multiple
      ? null
      : (options.find((option) => option.value === currentValue) ?? null);

    // cmdk only scrolls with `block: 'nearest'`, so filtering can leave the list
    // parked mid-list. Reset on query change; skip mount so open keeps its offset.
    const listRef = useRef<HTMLDivElement>(null);
    const activeQuery = onSearchChange ? (searchValue ?? '') : query;
    const previousQueryRef = useRef(activeQuery);
    useEffect(() => {
      if (previousQueryRef.current === activeQuery) {
        return;
      }
      previousQueryRef.current = activeQuery;
      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
    }, [activeQuery]);

    const { isCreateActionFocused, resetCreateActionFocus, handleCreateActionKeyDownCapture } =
      useCreateActionFocus({ createAction, activeQuery });

    // `nearest` pins the selection to an edge on open; centre it. rAF to land after cmdk.
    useEffect(() => {
      if (!open || !currentValue) {
        return undefined;
      }
      const frame = requestAnimationFrame(() => {
        listRef.current
          ?.querySelector(`[cmdk-item][data-option-value="${CSS.escape(currentValue)}"]`)
          ?.scrollIntoView({ block: 'center' });
      });
      return () => cancelAnimationFrame(frame);
    }, [open, currentValue]);

    const changeOpen = useCallback(
      (next: boolean) => {
        if (next) {
          setQuery('');
          resetCreateActionFocus();
        }
        if (openProp === undefined) {
          setInternalOpen(next);
        }
        onOpenChange?.(next);
      },
      [openProp, onOpenChange, resetCreateActionFocus],
    );

    const handleClose = useCallback(() => changeOpen(false), [changeOpen]);

    const hasValue = multiple ? values.length > 0 : isNonNullish(currentValue);
    const canClear = clearable && hasValue && !disabled && !triggerLoading;

    const handleClear = useCallback(() => {
      if (!isControlled) {
        setInternalValue(null);
      }
      onClear?.();
    }, [isControlled, onClear]);

    // Scrolling the sentinel row into view loads the next page. Callback ref:
    // the sentinel mounts inside the Popover portal a commit after `open`
    // flips, too late for an effect-based observer (useInViewTrigger) to see it.
    const loadMoreIO = useRef<IntersectionObserver | null>(null);
    const loadMoreRef = useCallback(
      (el: HTMLDivElement | null) => {
        loadMoreIO.current?.disconnect();
        loadMoreIO.current = null;
        if (!el || !onLoadMore || loadingMore) {
          return;
        }
        const io = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            onLoadMore();
          }
        });
        io.observe(el);
        loadMoreIO.current = io;
      },
      [onLoadMore, loadingMore],
    );

    const handleSelect = useCallback(
      (option: SelectMenuOption) => {
        if (option.onSelect) {
          option.onSelect();
          return;
        }

        if (multiple) {
          const nextValues = values.includes(option.value)
            ? values.filter((entry) => entry !== option.value)
            : [...values, option.value];
          onValuesChange?.(nextValues, option);
          return;
        }

        if (!isControlled) {
          setInternalValue(option.value);
        }
        onChange?.(option.value, option);
        handleClose();
      },
      [multiple, values, isControlled, onChange, onValuesChange, handleClose],
    );

    const getTriggerProps = useCallback(
      (): SelectMenuTriggerProps => ({
        ref: setAnchorEl,
        id: triggerId,
        disabled,
        'aria-haspopup': 'listbox',
        'aria-expanded': open,
        'aria-controls': open ? listboxId : undefined,
        'aria-disabled': disabled || undefined,
        onClick: () => {
          if (!disabled) {
            changeOpen(!open);
          }
        },
        onKeyDown: (event) => {
          if (
            !disabled &&
            hotkey &&
            event.key.toLowerCase() === hotkey.toLowerCase() &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.altKey
          ) {
            event.preventDefault();
            changeOpen(true);
          }
        },
      }),
      [triggerId, disabled, open, listboxId, hotkey, changeOpen],
    );

    // Digit hotkeys select a currently visible option even while the search
    // input has focus; unmatched digits fall through to the filter as text.
    // Runs in the capture phase so it beats cmdk and the input. The consumer
    // hook runs first and claims keys by preventing default.
    const handleMenuKeyDownCapture = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        onKeyDownCapture?.(event);
        if (event.defaultPrevented) {
          return;
        }

        if (handleCreateActionKeyDownCapture(event)) {
          return;
        }

        if (!/^[0-9]$/.test(event.key) || event.metaKey || event.ctrlKey || event.altKey) {
          return;
        }
        const item = event.currentTarget.querySelector<HTMLElement>(
          `[cmdk-item][data-hotkey="${event.key}"]`,
        );
        const optionValue = item?.getAttribute('data-option-value');
        const option = options.find(
          (candidate) => candidate.value === optionValue && !candidate.disabled,
        );
        if (option) {
          event.preventDefault();
          event.stopPropagation();
          handleSelect(option);
        }
      },
      [options, handleSelect, onKeyDownCapture, handleCreateActionKeyDownCapture],
    );

    // An explicit `triggerIcon` wins: server-backed menus render the trigger from
    // their own value, which can carry decoration (badges) the row icons don't.
    const leadingIcon = triggerIcon ?? selectedOption?.icon;

    const renderDefaultTrigger = () => {
      const { ref: anchorRef, ...triggerProps } = getTriggerProps();
      const triggerLabel =
        multiple && values.length
          ? `${values.length} selected`
          : (triggerLabelOverride ?? selectedOption?.title ?? placeholder);

      return (
        <SelectTrigger
          {...triggerProps}
          open={open}
          variant={variant}
          aria-label={triggerLabel === placeholder ? label : `${label}: ${triggerLabel}`}
          ref={(node: HTMLButtonElement | null) => {
            setRef(anchorRef, node);
            setRef(forwardedRef, node);
          }}
          sx={sx}
          icon={leadingIcon}
          iconColor={selectedOption?.iconColor}
          label={triggerLabel}
          content={triggerContent}
          loading={triggerLoading}
          onClear={canClear ? handleClear : undefined}
        />
      );
    };

    const trigger: ReactNode = renderTrigger
      ? renderTrigger({ open, selectedOption, disabled, getTriggerProps })
      : renderDefaultTrigger();

    const renderOption = (option: SelectMenuOption) => {
      const isSelected =
        !option.onSelect &&
        (multiple ? values.includes(option.value) : option.value === currentValue);
      const hasDescription = Boolean(option.description);

      return (
        <Command.Item
          key={option.value}
          // cmdk keys the active row by value (titles can collide); the title filters via keywords.
          value={option.value}
          keywords={[option.title, option.group ?? '', ...(option.keywords ?? [])].filter(
            Boolean,
          )}
          disabled={option.disabled}
          onSelect={() => handleSelect(option)}
          aria-label={option.title}
          // cmdk owns aria-selected (the highlight), so checked state goes on aria-checked.
          aria-checked={multiple && !option.onSelect ? isSelected : undefined}
          data-hotkey={option.hotkey}
          data-option-value={option.value}
          data-command={option.onSelect ? '' : undefined}
          onMouseEnter={(event: ReactMouseEvent<HTMLDivElement>) =>
            onOptionHover?.(option, event.currentTarget)
          }
          onMouseLeave={() => onOptionHoverEnd?.(option)}
          // Rows with a description align to the top (per the mockup); rows
          // without one center the title against the icon tile.
          style={hasDescription ? undefined : { alignItems: 'center' }}
        >
          <OptionRow
            icon={option.icon}
            iconTestId="select-menu-option-icon"
            iconSx={{
              ...(option.iconColor && {
                color: option.iconColor,
                backgroundColor: alpha(option.iconColor, 0.18),
              }),
              ...(plainOptionIcons && { backgroundColor: 'transparent' }),
            }}
            title={option.title}
            titleStart={
              option.group && !sectioned ? (
                <OptionHint>
                  {option.group}
                  <ChevronRight />
                </OptionHint>
              ) : null
            }
            titleEnd={
              <>
                {option.hint ? <OptionHint>{option.hint}</OptionHint> : null}
                {option.endIcon}
                {option.adornment ??
                  (multiple && !option.onSelect ? (
                    // Visibility lives in CommandRoot's stylesheet; the slot always stays.
                    <OptionCheckbox checked={isSelected} />
                  ) : null)}
                {!multiple && isSelected ? (
                  // `data-check` is the styling hook for call sites that retune the selected
                  // row through `menuSx`; the test id is for tests and must stay free to
                  // change without silently breaking someone's stylesheet.
                  <OptionCheck aria-hidden data-check="" data-testid="select-menu-check">
                    <Check />
                  </OptionCheck>
                ) : null}
              </>
            }
            description={option.description}
            endAdornment={
              option.hotkey ? (
                <HotkeyGlyph sx={{ marginTop: hasDescription ? '0.125rem' : 0 }}>
                  {option.hotkey}
                </HotkeyGlyph>
              ) : null
            }
          />
        </Command.Item>
      );
    };

    // Spinner only when empty — a refetch keeps the stale list so the menu doesn't flicker.
    const showLoading = loading && options.length === 0;

    return (
      <>
        {tooltip && !renderTrigger ? (
          <Tooltip title={tooltip}>{trigger as ReactElement}</Tooltip>
        ) : (
          trigger
        )}
        <Popover
          open={Boolean(open && anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          transitionDuration={transitionDuration}
          slotProps={{
            paper: {
              sx: {
                ...getDropdownPaperStyles(theme),
                marginTop: theme.typography.pxToRem(DROPDOWN_TRIGGER_GAP_PX),
                width:
                  menuWidth === 'trigger' ? (anchorEl?.offsetWidth ?? undefined) : menuWidth,
                maxWidth: 'calc(100vw - 2rem)',
                // The paper itself never scrolls — only [cmdk-list] does. On
                // short viewports MUI caps the paper height, so the flex chain
                // below hands the remaining space to the list.
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                ...menuSx,
              },
            },
          }}
        >
          <CommandRoot onKeyDownCapture={handleMenuKeyDownCapture}>
            <Command
              loop
              // Parent-controlled search owns filtering; cmdk only filters locally.
              shouldFilter={!!search && !onSearchChange}
              filter={substringFilter}
              label={label}
              defaultValue={selectedOption?.value}
            >
              {search ? (
                <SearchRow>
                  <Search aria-hidden />
                  <Command.Input
                    asChild
                    value={onSearchChange ? (searchValue ?? '') : query}
                    onValueChange={onSearchChange ?? setQuery}
                  >
                    <SearchInput autoFocus placeholder={searchPlaceholder} />
                  </Command.Input>
                  {hotkey ? <HotkeyGlyph>{hotkey}</HotkeyGlyph> : null}
                </SearchRow>
              ) : (
                <Command.Input autoFocus style={HIDDEN_INPUT_STYLE} aria-label={label} />
              )}
              {header ? <Box sx={{ flexShrink: 0 }}>{header}</Box> : null}
              <Command.List
                ref={listRef}
                id={listboxId}
                aria-multiselectable={multiple || undefined}
              >
                {showLoading ? (
                  <Command.Loading>
                    <MenuStatus data-testid="select-menu-loading">
                      <CircularProgress size="1rem" />
                      Loading…
                    </MenuStatus>
                  </Command.Loading>
                ) : (
                  <Command.Empty>
                    <MenuStatus>{emptyText}</MenuStatus>
                  </Command.Empty>
                )}
                {sectioned
                  ? groupRuns(options).map(([group, rows]) =>
                      group ? (
                        // Keyed by the run's first option, not the label: the same
                        // group can appear in more than one non-consecutive run.
                        <Command.Group key={rows[0].value} heading={group}>
                          {rows.map(renderOption)}
                        </Command.Group>
                      ) : (
                        rows.map(renderOption)
                      ),
                    )
                  : options.map(renderOption)}
                {hasMore ? (
                  <MenuStatus ref={loadMoreRef} data-testid="select-menu-load-more">
                    <CircularProgress size="1rem" />
                    Loading…
                  </MenuStatus>
                ) : null}
              </Command.List>
              {createAction ? (
                <>
                  <CreateActionDivider />
                  <CreateActionRow
                    type="button"
                    data-active={isCreateActionFocused || undefined}
                    onClick={() => createAction.onSelect()}
                  >
                    <Plus aria-hidden />
                    {createAction.label}
                  </CreateActionRow>
                </>
              ) : null}
            </Command>
          </CommandRoot>
          {children}
        </Popover>
      </>
    );
  },
);

SelectMenu.displayName = 'SelectMenu';
